import { Component } from '@theme/component';

/**
 * Toggles the "Point rouge" tooltip on click/tap (CSS `:hover` already handles the desktop hover
 * case on its own — see `snippets/price.liquid`). Closes on a click outside the component.
 *
 * @extends {Component<{trigger: HTMLButtonElement}>}
 */
class ProductRedDot extends Component {
  /** @param {Event} _event */
  toggle = (_event) => {
    const { trigger } = this.refs;
    const expanding = trigger.getAttribute('aria-expanded') !== 'true';

    trigger.setAttribute('aria-expanded', String(expanding));

    if (expanding) {
      document.addEventListener('click', this.#handleOutsideClick);
    }
  };

  /** @param {MouseEvent} event */
  #handleOutsideClick = (event) => {
    if (event.target instanceof Node && this.contains(event.target)) return;

    this.refs.trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this.#handleOutsideClick);
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#handleOutsideClick);
  }
}

/**
 * WCAG 1.4.13: a tooltip must be dismissible without moving the pointer or the focus. One delegated
 * listener rather than one per component — a listing carries a red dot per card.
 *
 * Two states to undo: the `aria-expanded` of the tap/click case, and the CSS `:hover` of the desktop
 * one, which no JS state drives. `data-dismissed` suppresses the latter until the pointer leaves,
 * where `pointerleave` clears it (cf. `snippets/price.liquid`).
 */
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;

  for (const dot of document.querySelectorAll('product-red-dot')) {
    const trigger = dot.querySelector('.price-red-dot__trigger');
    const expanded = trigger?.getAttribute('aria-expanded') === 'true';
    const hovered = dot.matches(':hover');
    if (!expanded && !hovered) continue;

    trigger?.setAttribute('aria-expanded', 'false');

    if (hovered) {
      dot.setAttribute('data-dismissed', '');
      dot.addEventListener('pointerleave', () => dot.removeAttribute('data-dismissed'), { once: true });
    }
  }
});

if (!customElements.get('product-red-dot')) {
  customElements.define('product-red-dot', ProductRedDot);
}
